import React, { useState, useEffect, useRef, useCallback } from "react";
import { MdRemove } from "react-icons/md";
import Message from "./Message";
import TypingIndicator from "./TypingIndicator";
import FloatingButton from "./FloatingButton";
import JotaAvatar from "./JotaAvatar";
import ChatInput from "./ChatInput";
import JotaImage from "assets/img/jota.png";
import useJotaWebSocket from "hooks/useJotaWebSocket";
import {
	ChatContainer,
	ChatHeader,
	HeaderLeft,
	AvatarSmall,
	HeaderInfo,
	HeaderTitle,
	HeaderStatus,
	StatusDot,
	CloseButton,
	ChatBody,
	WelcomeContainer,
	AvatarLarge,
	WelcomeTitle,
	WelcomeSubtitle,
	WelcomeQuestion,
	WelcomeHint,
	MessagesContainer,
	ChatFooter
} from "./styles";

const SESSION_STORAGE_KEY = "jota_chat_session_erp";

const JotaChat = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState([]);
	const [inputValue, setInputValue] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const [hasStartedChat, setHasStartedChat] = useState(false);

	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);
	const responseTimeoutRef = useRef(null);
	const endSessionRef = useRef(null);

	const resetChat = useCallback(() => {
		if (responseTimeoutRef.current) {
			clearTimeout(responseTimeoutRef.current);
			responseTimeoutRef.current = null;
		}
		setMessages([]);
		setHasStartedChat(false);
		setInputValue("");
		setIsTyping(false);
		setIsOpen(false);
		sessionStorage.removeItem(SESSION_STORAGE_KEY);
	}, []);

	const generateMessageId = () => {
		return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	};

	const handleIncomingMessage = useCallback(data => {
		setIsTyping(false);

		if (responseTimeoutRef.current) {
			clearTimeout(responseTimeoutRef.current);
			responseTimeoutRef.current = null;
		}

		if (!data) return;

		let newMessage = null;

		if (data.type === "message" || data.type === "text") {
			newMessage = {
				id: generateMessageId(),
				content: data.message || data.value || "",
				type: "message",
				isUser: false,
				sender: "JOTA",
				showFeedback: !!data.messageId,
				messageId: data.messageId,
				topic: data.topic
			};
		} else if (data.type === "link") {
			newMessage = {
				id: generateMessageId(),
				content: data.message || data.value || "",
				label: data.label || "Clique aqui",
				type: "link",
				isUser: false,
				sender: "JOTA",
				showFeedback: !!data.messageId,
				messageId: data.messageId,
				topic: data.topic
			};
		}

		if (newMessage) {
			setMessages(prev => [...prev, newMessage]);
		}
	}, []);

	const handleError = useCallback(() => {
		const errorMessage = {
			id: generateMessageId(),
			content:
				"Desculpe, estou com uma instabilidade momentânea e não consegui processar sua mensagem.",
			type: "message",
			isUser: false,
			sender: "JOTA",
			showFeedback: false
		};

		setMessages(prev => [...prev, errorMessage]);
		setIsTyping(false);
	}, []);

	const {
		connectionStatus,
		isSessionReady,
		sendMessage,
		sendFeedback,
		sendTransshipment,
		endSession
	} = useJotaWebSocket(
		handleIncomingMessage,
		isOpen,
		resetChat,
		handleError
	);

	endSessionRef.current = endSession;

	useEffect(() => {
		const savedSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (savedSession) {
			try {
				const parsed = JSON.parse(savedSession);
				setMessages(parsed.messages || []);
				setHasStartedChat(parsed.messages?.length > 0);
			} catch {
				// Erro ao parsear sessão salva
			}
		}
	}, []);

	useEffect(() => {
		if (messages.length > 0) {
			sessionStorage.setItem(
				SESSION_STORAGE_KEY,
				JSON.stringify({
					messages,
					lastUpdated: Date.now()
				})
			);
		}
	}, [messages]);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, isTyping]);

	useEffect(() => {
		if (isOpen && inputRef.current) {
			inputRef.current.focus();
		}
		if (isOpen && messages.length > 0) {
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
			}, 100);
		}
	}, [isOpen, messages.length]);

	const handleSendMessage = useCallback(
		async content => {
			if (!content.trim()) return;

			const userMessage = {
				id: generateMessageId(),
				content: content.trim(),
				isUser: true
			};

			setMessages(prev => [...prev, userMessage]);
			setInputValue("");
			setHasStartedChat(true);
			setIsTyping(true);

			if (responseTimeoutRef.current) {
				clearTimeout(responseTimeoutRef.current);
			}

			responseTimeoutRef.current = setTimeout(() => {
				setIsTyping(false);
				handleError();
			}, 60000);

			const success = await sendMessage(content.trim());
			if (!success) {
				if (responseTimeoutRef.current) {
					clearTimeout(responseTimeoutRef.current);
					responseTimeoutRef.current = null;
				}
				// Simula digitação por 1.5s antes de exibir erro
				setTimeout(() => {
					setIsTyping(false);
					handleError();
				}, 1500);
			}
		},
		[sendMessage, handleError]
	);

	const handleFeedback = async (messageId, feedbackType) => {
		setMessages(prev =>
			prev.map(msg =>
				msg.id === messageId || msg.messageId === messageId
					? { ...msg, feedback: feedbackType }
					: msg
			)
		);

		await sendFeedback(messageId, feedbackType);
	};

	const handleWhatsAppRedirect = useCallback(async () => {
		try {
			await sendTransshipment();
		} catch (error) {
			console.error("[Transbordo] Erro ao enviar transbordo:", error);
		}

		if (endSessionRef.current) {
			endSessionRef.current();
		}

		resetChat();
	}, [sendTransshipment, resetChat]);

	const handleKeyPress = e => {
		if (e.key === "Enter" && !e.shiftKey && !isTyping && isSessionReady && connectionStatus === "connected") {
			e.preventDefault();
			handleSendMessage(inputValue);
		}
	};

	const toggleChat = () => {
		setIsOpen(prev => !prev);
	};

	useEffect(() => {
		return () => {
			if (responseTimeoutRef.current) {
				clearTimeout(responseTimeoutRef.current);
			}
			if (endSessionRef.current) {
				endSessionRef.current();
			}
			resetChat();
		};
	}, [resetChat]);

	return (
		<>
			<FloatingButton isOpen={isOpen} onClick={toggleChat} />

			{isOpen && (
				<ChatContainer role="dialog" aria-label="Chat com JOTA">
					<ChatHeader>
						<HeaderLeft>
							<AvatarSmall>
								<JotaAvatar size={40} />
							</AvatarSmall>
							<HeaderInfo>
								<HeaderTitle>JOTA</HeaderTitle>
								<HeaderStatus>
									<StatusDot status={connectionStatus} />
									{connectionStatus === "connected"
										? "Online"
										: connectionStatus === "connecting"
										? "Conectando..."
										: "Offline"}
								</HeaderStatus>
							</HeaderInfo>
						</HeaderLeft>
						<CloseButton onClick={toggleChat} aria-label="Minimizar chat">
							<MdRemove />
						</CloseButton>
					</ChatHeader>

					<ChatBody>
						{!hasStartedChat ? (
							<>
								<WelcomeContainer>
									<AvatarLarge bgImage={JotaImage} />
									<WelcomeTitle>Sou o JOTA, seu novo assistente!</WelcomeTitle>
									<WelcomeSubtitle>
										Estou aqui para simplificar a gestão da sua empresa.
									</WelcomeSubtitle>
									<WelcomeQuestion>
										Como posso ajudar você hoje?
									</WelcomeQuestion>
								</WelcomeContainer>
							</>
						) : (
							<MessagesContainer>
								{messages.map(message => (
									<Message
										key={message.id}
										message={message}
										onFeedback={handleFeedback}
										onWhatsAppRedirect={handleWhatsAppRedirect}
									/>
								))}
								{isTyping && <TypingIndicator />}
								<div ref={messagesEndRef} />
							</MessagesContainer>
						)}
					</ChatBody>

					<ChatFooter>
						<ChatInput
							value={inputValue}
							onChange={e => setInputValue(e.target.value)}
							onSend={() => handleSendMessage(inputValue)}
							onKeyPress={handleKeyPress}
							inputRef={inputRef}
							disabled={isTyping || !isSessionReady || connectionStatus !== "connected"}
						/>
					</ChatFooter>
				</ChatContainer>
			)}
		</>
	);
};

export default JotaChat;
